import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

export const ReportSalePage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const res = await api.get(`/packages/for-pharmacy/${user.pharmacy_id}`);
      setPackages(res.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handleConfirmSale = async () => {
    if (!selectedPackage) return;
    
    setLoading(true);
    try {
      await api.post('/sales', {
        pharmacy_id: user.pharmacy_id,
        package_id: selectedPackage.id,
        quantity
      });
      toast.success('Sale reported successfully');
      setSelectedPackage(null);
      setQuantity(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report sale');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>
        {t('nav.reportSale')}
      </h1>

      {!selectedPackage ? (
        <div className="space-y-4">
          <p className="text-slate-600 mb-4">Select a package to report sale:</p>
          {packages.map((pkg) => {
            const nonFoc = (pkg.line_items || []).filter(li => !li.is_foc).reduce((s, li) => s + (li.quantity || 0), 0);
            const foc = (pkg.line_items || []).filter(li => li.is_foc).reduce((s, li) => s + (li.quantity || 0), 0);
            const unit = 'unit'; // Unit differs per product; use generic label — never sum across products
            const deductsLabel = foc > 0 ? `deducts ${nonFoc + foc} items (${foc} free)` : `deducts ${nonFoc} item${nonFoc !== 1 ? 's' : ''}`;
            return (
              <Card
                key={pkg.id}
                data-testid={`package-card-${pkg.id}`}
                onClick={() => setSelectedPackage(pkg)}
                className="p-6 cursor-pointer hover:shadow-md transition-all border-2 border-transparent hover:border-blue-500"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>{pkg.name}</h3>
                    <p className="text-slate-600 mt-1 text-sm">{deductsLabel}</p>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: '#1E3A8A' }}>RM {pkg.price?.toFixed(2)}</p>
                </div>
              </Card>
            );
          })}
          {packages.length === 0 && (
            <p className="text-center text-slate-500 py-8">{t('common.noData')}</p>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <Card className="p-6 bg-white">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>{selectedPackage.name}</h3>
            <p className="text-3xl font-bold mb-4" style={{ color: '#1E3A8A' }}>RM {selectedPackage.price?.toFixed(2)}</p>
          </Card>

          <Card className="p-6 bg-white">
            <label className="block text-sm font-semibold mb-2">Quantity</label>
            <div className="flex items-center gap-4">
              <Button
                data-testid="qty-minus-btn"
                variant="outline"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-12 h-12 text-xl"
              >
                -
              </Button>
              <span className="text-3xl font-bold w-16 text-center" data-testid="quantity-display">{quantity}</span>
              <Button
                data-testid="qty-plus-btn"
                variant="outline"
                onClick={() => setQuantity(quantity + 1)}
                className="w-12 h-12 text-xl"
              >
                +
              </Button>
            </div>
          </Card>

          <Card className="p-6 bg-blue-50">
            <div className="flex justify-between items-center text-lg">
              <span className="font-semibold">Total:</span>
              <span className="text-2xl font-bold" style={{ color: '#1E3A8A' }}>
                RM {(selectedPackage.price * quantity).toFixed(2)}
              </span>
            </div>
          </Card>

          <div className="flex gap-4">
            <Button
              data-testid="cancel-sale-btn"
              variant="outline"
              onClick={() => {
                setSelectedPackage(null);
                setQuantity(1);
              }}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="confirm-sale-btn"
              onClick={handleConfirmSale}
              disabled={loading}
              className="flex-1"
              style={{ backgroundColor: '#1E3A8A' }}
            >
              {loading ? t('common.loading') : t('common.confirm')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};